import { useState } from 'react'
import { isRole, roleHasCapability } from '@qim/shared'
import { fetchAccountCost, fetchInstanceCosts } from '../api.js'
import { formatInstant, formatMoney } from '../format.js'
import type { AccountCostWire, CostPeriodWire, InstanceCostsWire } from '../api.js'
import type { JSX } from 'react'

/**
 * What this cloud account is spending (IM-130).
 *
 * ## Why there is a button
 *
 * `GetCostAndUsage` charges the **customer's** account about a cent per call.
 * A figure fetched when this panel rendered would be a bill that grows with how
 * often anyone opens the console, and it would grow fastest for the customer
 * whose account somebody is worried about. So it is asked for.
 *
 * The API caches the answer against the period, so asking twice in an afternoon
 * costs once — but the first ask is still a real charge, and the button is what
 * makes that a decision rather than a side effect of navigation.
 *
 * ## Why the period is on screen
 *
 * Cost Explorer's data lags roughly a day, so "now" does not exist. A number
 * without the window it covers invites being read as a live rate, and it never
 * is one. The period is rendered beside the figure, always, and so is when it
 * was read — a cached answer that says nothing about its age is one people stop
 * trusting at exactly the wrong moment.
 *
 * ## Why the breakdown is a second button (IM-131)
 *
 * It is a second charged request, and the question is asked much less often —
 * somebody wants the total far more often than they want the split. Making it
 * one button would charge for both every time.
 *
 * ## Why unreadable is never zero
 *
 * An account whose access stack predates bootstrap v6 has no permission to be
 * asked. It spends money like any other, and rendering that as $0.00 would be a
 * confident lie somebody could plan against. The reason is printed instead, and
 * it names the fix.
 */

const canRead = (role: string): boolean => isRole(role) && roleHasCapability(role, 'cost:read')

const Period = ({ period }: { readonly period: CostPeriodWire }): JSX.Element => (
  <span className="muted">{period.describes ?? `${period.from} to ${period.to}`}</span>
)

export const AccountCost = ({
  baseUrl,
  tenantId,
  accountKey,
  role,
  title = 'Cost',
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly role: string
  /** The heading, so a customer's Cost tab can say which account each panel is. */
  readonly title?: string
}): JSX.Element | null => {
  const [answer, setAnswer] = useState<
    { readonly period: CostPeriodWire; readonly cost: AccountCostWire } | undefined
  >(undefined)
  const [breakdown, setBreakdown] = useState<
    { readonly period: CostPeriodWire; readonly costs: InstanceCostsWire } | undefined
  >(undefined)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  // Not a control — the API checks `cost:read` regardless. Offering a button
  // that always fails is a worse way to say no than not offering it.
  if (!canRead(role)) return null

  const read = async (): Promise<void> => {
    setBusy(true)
    setProblem(undefined)
    try {
      setAnswer(await fetchAccountCost(baseUrl, tenantId, accountKey))
    } catch {
      setProblem('Could not read this account’s costs just now.')
    } finally {
      setBusy(false)
    }
  }

  const readBreakdown = async (): Promise<void> => {
    setBusy(true)
    setProblem(undefined)
    try {
      setBreakdown(await fetchInstanceCosts(baseUrl, tenantId, accountKey))
    } catch {
      setProblem('Could not read the per-instance breakdown just now.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="panel">
      <h2>{title}</h2>

      <button
        type="button"
        className="ghost"
        disabled={busy}
        onClick={() => {
          void read()
        }}
      >
        {answer === undefined ? 'Read this account’s cost' : 'Read again'}
      </button>

      <p className="hint">
        Reads AWS Cost Explorer in the customer&rsquo;s account, which charges them about a cent per
        request &mdash; so it happens when you ask. The answer is cached for the period it covers,
        and the data is about a day behind.
      </p>

      {busy ? <p role="status">Reading…</p> : null}

      {answer === undefined ? null : answer.cost.state === 'read' ? (
        <dl className="detail">
          <dt>Spend</dt>
          <dd>
            <strong>{formatMoney(answer.cost.amount, answer.cost.currency)}</strong>{' '}
            <Period period={answer.period} />
          </dd>
          <dt>Read</dt>
          {/* Said, because a cached answer that hides its age is one people stop
            trusting at exactly the wrong moment. */}
          <dd className="muted">{formatInstant(answer.cost.readAt)}</dd>
        </dl>
      ) : (
        /*
         * Not zero, and not styled as a failure either. The commonest cause is
         * an access stack that predates v6, which is an ordinary state of a
         * customer account rather than something broken.
         */
        <p role="status" className="hint">
          {answer.cost.reason}
        </p>
      )}

      <h3>Per instance</h3>

      <button
        type="button"
        className="ghost"
        disabled={busy}
        onClick={() => {
          void readBreakdown()
        }}
      >
        {breakdown === undefined ? 'Break it down by instance' : 'Read again'}
      </button>

      {breakdown === undefined ? null : breakdown.costs.state === 'read' ? (
        <Breakdown costs={breakdown.costs} />
      ) : (
        <p role="status" className="hint">
          {breakdown.costs.reason}
        </p>
      )}

      {problem === undefined ? null : (
        <p role="alert" className="failure">
          {problem}
        </p>
      )}
    </section>
  )
}

/**
 * The per-instance table, narrowed once.
 *
 * Its own component so the union is discriminated at the boundary rather than
 * in every cell — the remainder row reads `unattributed` and the currency, and
 * re-narrowing inside a `<td>` is how a cast gets written.
 */
const Breakdown = ({
  costs,
}: {
  readonly costs: Extract<InstanceCostsWire, { state: 'read' }>
}): JSX.Element => (
  <>
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th scope="col">Instance</th>
            <th scope="col">Spend</th>
          </tr>
        </thead>
        <tbody>
          {costs.instances.map((line) => (
            <tr key={line.deploymentId}>
              <td className="mono-cell">{line.deploymentId}</td>
              <td>{formatMoney(line.amount, costs.currency)}</td>
            </tr>
          ))}
          {/*
                  Its own row, always — even at zero, because a breakdown that
                  hides the remainder reads as complete when it is not. AWS
                  reports it rather than this subtracting for it, and in the test
                  account it was 17% of the month.
                */}
          <tr>
            <td>
              Not attributable to an instance
              <p className="muted">
                The EKS control plane, NAT and data transfer, anything shared between instances, and
                anything made outside Terraform.
              </p>
            </td>
            <td>{formatMoney(costs.unattributed, costs.currency)}</td>
          </tr>
        </tbody>
      </table>
    </div>
    {costs.truncated === true ? (
      <p role="alert" className="failure">
        AWS had more instances than one page held, so this breakdown is short by an unknown amount.
        The account total above is still complete.
      </p>
    ) : null}
  </>
)
